import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Send, User, X, ArrowLeft, Trash2, Bookmark, Folder, FolderPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useNavigate } from 'react-router-dom';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Messages = ({ user }) => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leftTab, setLeftTab] = useState('chats'); // 'chats' | 'folders'
  const [folders, setFolders] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderMessages, setFolderMessages] = useState([]);
  const [savingMsg, setSavingMsg] = useState(null); // message being saved to a folder

  useEffect(() => {
    if (user.role !== 'admin' && user.role !== 'supervisor' && user.role !== 'employee' && user.role !== 'sub_contractor' && user.role !== 'client') {
      toast.error('Messages are not available for your role');
      navigate('/dashboard');
      return;
    }
    fetchConversations();
    fetchAllUsers();
    fetchFolders();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser.user_id);
      markMessagesAsRead(selectedUser.user_id);
    }
  }, [selectedUser]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/direct-messages/conversations`);
      setConversations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch conversations');
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      let filteredUsers;
      
      if ((user.role === 'admin' || user.role === 'supervisor')) {
        // Admins can message everyone except themselves
        filteredUsers = response.data.filter(u => u.id !== user.id);
      } else if (user.role === 'employee') {
        // Employees can message admins and other employees
        filteredUsers = response.data.filter(
          u => (u.role === 'admin' || u.role === 'employee') && u.id !== user.id
        );
      } else if (user.role === 'client') {
        // Clients can only message admins
        filteredUsers = response.data.filter(u => u.role === 'admin');
      } else {
        filteredUsers = [];
      }
      
      setAllUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await axios.get(`${API}/direct-messages/conversation/${userId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages');
    }
  };

  const markMessagesAsRead = async (userId) => {
    try {
      const response = await axios.get(`${API}/direct-messages/conversation/${userId}`);
      const unreadMessages = response.data.filter(
        msg => msg.receiver_id === user.id && !msg.is_read
      );
      
      for (const msg of unreadMessages) {
        await axios.post(`${API}/direct-messages/${msg.id}/mark-read`);
      }
      
      fetchConversations();
    } catch (error) {
      console.error('Failed to mark messages as read');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    try {
      await axios.post(`${API}/direct-messages`, {
        receiver_id: selectedUser.user_id,
        content: newMessage
      });
      setNewMessage('');
      fetchMessages(selectedUser.user_id);
      fetchConversations();
      toast.success('Message sent!');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const isAdmin = user.role === 'admin' || user.role === 'supervisor';

  const fetchFolders = async () => {
    try {
      const { data } = await axios.get(`${API}/message-folders`);
      setFolders(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const { data } = await axios.post(`${API}/message-folders`, { name });
      setFolders((f) => [...f, data]);
      setNewFolderName('');
      toast.success(`Folder "${data.name}" created`);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create folder');
    }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}" and everything saved in it?`)) return;
    try {
      await axios.delete(`${API}/message-folders/${folder.id}`);
      setFolders((f) => f.filter((x) => x.id !== folder.id));
      if (selectedFolder?.id === folder.id) { setSelectedFolder(null); setFolderMessages([]); }
      toast.success('Folder deleted');
    } catch {
      toast.error('Could not delete folder');
    }
  };

  const openFolder = async (folder) => {
    setSelectedFolder(folder);
    setSelectedUser(null);
    try {
      const { data } = await axios.get(`${API}/message-folders/${folder.id}/messages`);
      setFolderMessages(Array.isArray(data) ? data : []);
    } catch {
      setFolderMessages([]);
    }
  };

  const saveToFolder = async (folderId) => {
    try {
      await axios.post(`${API}/message-folders/${folderId}/save`, { message_id: savingMsg.id });
      toast.success('Message saved to folder');
      setSavingMsg(null);
      fetchFolders();
    } catch {
      toast.error('Could not save message');
    }
  };

  const removeSaved = async (savedId) => {
    try {
      await axios.delete(`${API}/saved-messages/${savedId}`);
      setFolderMessages((m) => m.filter((x) => x.id !== savedId));
      fetchFolders();
      toast.success('Removed from folder');
    } catch {
      toast.error('Could not remove');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    const confirmText = isAdmin
      ? 'Delete this message for everyone? This cannot be undone.'
      : 'Remove this message from your view? Others can still see it.';
    if (!window.confirm(confirmText)) return;
    try {
      if (isAdmin) {
        await axios.delete(`${API}/direct-messages/${messageId}`);
      } else {
        await axios.post(`${API}/direct-messages/${messageId}/hide`);
      }
      setMessages((m) => m.filter((x) => x.id !== messageId));
      fetchConversations();
      toast.success(isAdmin ? 'Message deleted' : 'Message removed from your view');
    } catch {
      toast.error('Failed to delete message');
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser({
      user_id: user.id,
      user_name: user.name,
      user_role: user.role
    });
    setShowNewMessage(false);
  };

  const handleSelectConversation = (conv) => {
    setSelectedUser(conv);
    setSelectedFolder(null);
    setShowNewMessage(false);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Messages</h1>
            <p className="text-sm sm:text-base text-gray-600">Chat with your team members</p>
          </div>
        </div>

        {/* Messages Interface */}
        <div className="glass-card p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[600px]">
            {/* Conversations List */}
            <div className="md:col-span-1 border border-slate-300 rounded-lg overflow-hidden flex flex-col bg-white">
              <div className="p-3 bg-slate-100 border-b border-slate-300 flex justify-between items-center">
                <div className="flex gap-1">
                  <button
                    onClick={() => setLeftTab('chats')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${leftTab === 'chats' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-slate-200'}`}
                    data-testid="messages-tab-chats"
                  >
                    Chats
                  </button>
                  <button
                    onClick={() => setLeftTab('folders')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1 ${leftTab === 'folders' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-slate-200'}`}
                    data-testid="messages-tab-folders"
                  >
                    <Folder className="w-3.5 h-3.5" /> Folders
                  </button>
                </div>
                {leftTab === 'chats' && (
                  <Button
                    size="sm"
                    onClick={() => setShowNewMessage(!showNewMessage)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <MessageSquare className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {showNewMessage && (
                <div className="p-3 bg-slate-50 border-b border-slate-300">
                  <p className="text-xs text-slate-600 mb-2">Select a user to message:</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {allUsers.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => handleSelectUser(u)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-200 rounded text-sm text-gray-900"
                      >
                        {u.name} ({u.role})
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-y-auto">
                {leftTab === 'folders' ? (
                  <div className="p-3 space-y-2">
                    <div className="flex gap-1.5">
                      <input
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); }}
                        placeholder="New folder name…"
                        className="flex-1 px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm"
                        data-testid="new-folder-input"
                      />
                      <Button size="sm" onClick={createFolder} disabled={!newFolderName.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="create-folder-btn">
                        <FolderPlus className="w-4 h-4" />
                      </Button>
                    </div>
                    {folders.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-6">No folders yet — create one to save important messages</p>
                    ) : (
                      folders.map((f) => (
                        <div
                          key={f.id}
                          onClick={() => openFolder(f)}
                          className={`p-2.5 rounded-lg cursor-pointer flex items-center justify-between gap-2 border ${selectedFolder?.id === f.id ? 'bg-blue-50 border-blue-300' : 'border-slate-200 hover:bg-slate-100'}`}
                          data-testid={`folder-${f.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Folder className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            <span className="text-sm font-semibold text-gray-900 truncate">{f.name}</span>
                            <span className="text-xs text-gray-500">({f.count})</span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteFolder(f); }}
                            className="p-1 text-gray-400 hover:text-red-500"
                            data-testid={`delete-folder-${f.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                ) : loading ? (
                  <div className="p-4 text-center text-gray-600">Loading...</div>
                ) : conversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-600">
                    <p>No conversations yet</p>
                    <p className="text-xs mt-2">Click + to start messaging</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.user_id}
                      onClick={() => handleSelectConversation(conv)}
                      className={`p-3 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition ${
                        selectedUser?.user_id === conv.user_id ? 'bg-slate-200' : ''
                      }`}
                      data-testid={`conversation-${conv.user_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900 truncate">{conv.user_name}</p>
                            {conv.unread_count > 0 && (
                              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-600 rounded-full">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 truncate">{conv.last_message}</p>
                        </div>
                        <span className="text-xs text-gray-500 ml-2">
                          {formatTime(conv.last_message_time)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Message Thread */}
            <div className="md:col-span-2 border border-slate-300 rounded-lg overflow-hidden flex flex-col bg-white">
              {selectedFolder ? (
                <>
                  <div className="p-4 bg-slate-100 border-b border-slate-300 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Folder className="w-5 h-5 text-amber-500" />
                      <h3 className="font-semibold text-gray-900">{selectedFolder.name}</h3>
                      <span className="text-xs text-gray-500">{folderMessages.length} saved</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedFolder(null); setFolderMessages([]); }} className="text-gray-600 hover:text-gray-900">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="folder-messages">
                    {folderMessages.length === 0 ? (
                      <div className="text-center text-gray-600 mt-8">
                        <Bookmark className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                        <p>Nothing saved here yet</p>
                        <p className="text-xs text-gray-500 mt-1">Tap the bookmark icon on any message to save it</p>
                      </div>
                    ) : (
                      folderMessages.map((sm) => (
                        <div key={sm.id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-2" data-testid={`saved-msg-${sm.id}`}>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-500 mb-1">
                              <strong>{sm.sender_name}</strong> → {sm.receiver_name} · {formatTime(sm.message_created_at)}
                            </p>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{sm.content}</p>
                          </div>
                          <button
                            onClick={() => removeSaved(sm.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 flex-shrink-0"
                            title="Remove from folder"
                            data-testid={`remove-saved-${sm.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : selectedUser ? (
                <>
                  {/* Header */}
                  <div className="p-4 bg-slate-100 border-b border-slate-300 flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-gray-900">{selectedUser.user_name}</h3>
                      <p className="text-xs text-gray-600 capitalize">{selectedUser.user_role}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedUser(null)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-600 mt-8">
                        <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isSent = msg.sender_id === user.id;
                        return (
                          <div
                            key={msg.id}
                            className={`flex items-center gap-1 ${isSent ? 'justify-end' : 'justify-start'}`}
                          >
                            {isSent && (
                              <div className="flex items-center">
                                <button
                                  onClick={() => setSavingMsg(msg)}
                                  className="p-1.5 rounded-full text-gray-400 hover:text-amber-500 hover:bg-amber-50"
                                  title="Save to folder"
                                  data-testid={`save-dm-${msg.id}`}
                                >
                                  <Bookmark className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteMessage(msg.id)}
                                  className="p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50"
                                  title={isAdmin ? 'Delete for everyone' : 'Remove from my view'}
                                  data-testid={`delete-dm-${msg.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                            <div
                              className={`max-w-[70%] rounded-lg p-3 ${
                                isSent
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white border border-slate-300 text-gray-900'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                              <p
                                className={`text-xs mt-1 ${
                                  isSent ? 'text-blue-200' : 'text-gray-500'
                                }`}
                              >
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                            {!isSent && (
                              <div className="flex items-center">
                                <button
                                  onClick={() => setSavingMsg(msg)}
                                  className="p-1.5 rounded-full text-gray-400 hover:text-amber-500 hover:bg-amber-50"
                                  title="Save to folder"
                                  data-testid={`save-dm-${msg.id}`}
                                >
                                  <Bookmark className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteMessage(msg.id)}
                                  className="p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50"
                                  title={isAdmin ? 'Delete for everyone' : 'Remove from my view'}
                                  data-testid={`delete-dm-${msg.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Send Message Form */}
                  <form onSubmit={handleSendMessage} className="p-4 bg-slate-100 border-t border-slate-300">
                    <div className="flex gap-2">
                      <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 min-h-[60px] max-h-[120px] resize-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e);
                          }
                        }}
                      />
                      <Button
                        type="submit"
                        disabled={!newMessage.trim()}
                        className="bg-blue-600 hover:bg-blue-700 self-end"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">Press Enter to send, Shift+Enter for new line</p>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-600">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p>Select a conversation to view messages</p>
                    <p className="text-sm text-gray-500 mt-2">or start a new conversation</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save-to-folder picker */}
        {savingMsg && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setSavingMsg(null)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="save-folder-picker">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-amber-500" /> Save to folder
                </h3>
                <button onClick={() => setSavingMsg(null)} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-xs text-gray-500 mb-3 line-clamp-2 bg-gray-50 border border-gray-200 rounded-lg p-2">&quot;{savingMsg.content}&quot;</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto mb-3">
                {folders.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-3">No folders yet — create one below</p>
                ) : (
                  folders.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => saveToFolder(f.id)}
                      className="w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 hover:bg-amber-50 hover:border-amber-300 flex items-center gap-2 text-sm font-semibold text-gray-800"
                      data-testid={`pick-folder-${f.id}`}
                    >
                      <Folder className="w-4 h-4 text-amber-500" /> {f.name}
                      <span className="text-xs text-gray-400 ml-auto">{f.count}</span>
                    </button>
                  ))
                )}
              </div>
              <div className="flex gap-1.5">
                <input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const created = await createFolder();
                      if (created) saveToFolder(created.id);
                    }
                  }}
                  placeholder="Or create a new folder…"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="picker-new-folder-input"
                />
                <Button
                  size="sm"
                  onClick={async () => {
                    const created = await createFolder();
                    if (created) saveToFolder(created.id);
                  }}
                  disabled={!newFolderName.trim()}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="picker-create-save-btn"
                >
                  <FolderPlus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
